/**
 * What a check-in response says happened, and who it landed on. Pure routing over the response shape —
 * no policy, no transport — kept out of the contract barrel because this is the part that needs the
 * server's behaviour explained rather than just its field names.
 *
 * The invariants it is written against, which are the only ones it needs:
 * - `CreatedCustomerId`, `CheckedInShipmentId` and `ValidatedIdentificationCustomer` are independently
 *   nullable: any combination can arrive, including all three at once.
 * - `ValidatedIdentificationCustomer.Id` is 0 when the scan matched nobody, so only a positive id names an
 *   existing profile.
 * - `ValidatedMatchedCustomers` can be absent even when someone was checked in: an unambiguous single match
 *   comes back described in `ValidatedIdentificationCustomer` with no list at all.
 *
 * Nothing here reads the server's `AutoCreateWithIdScan` / `AutoCheckInWithIdScan` policy. The response
 * says what happened, so the four shapes those settings produce are routed rather than predicted.
 *
 * Re-exported by `camera-id-scan.ts`, which stays the single import site for both surfaces.
 */
import type { ScanGuestEntryResponse, ScanValidatedCustomer } from './camera-id-scan';
import type { IdPhotoPresence } from './id-photo-presence';

/**
 * The minimum an entry response has to expose to say who it landed on.
 *
 * Two fields here are read only by `id-photo-presence.ts`: each candidate's front-of-ID photo
 * presence, and the server's `ResolvedCustomerId` anchor. Routing deliberately reads neither.
 * `decodeScanEntry` answers "what can staff do with this response", which the four
 * AutoCreate x AutoCheckIn shapes already say without an identity anchor, and the manual photo
 * opt-in that ships today resolves its own target through `enteredCustomerId` below — unchanged, so
 * the anchor changes nothing that is live. They are declared here because this is the one shape
 * both questions are asked of.
 */
export type EnteredEntry = {
  CheckedInShipmentId?: number | null;
  CreatedCustomerId?: number | null;
  /**
   * The server's own answer for who the scan resolved to. Null-omitted on the wire, so absent means
   * either "staff still have to choose" or "this backend predates the field".
   * `frontPhotoTarget` documents why those two collapse safely into one refusal.
   */
  ResolvedCustomerId?: number | null;
  ValidatedMatchedCustomers?: ({ Id: number; IsBanned: boolean } & IdPhotoPresence)[] | null;
  /** The customer the scan resolved to. Id is 0 when the scan matched nobody. */
  ValidatedIdentificationCustomer?: ({ Id?: number | null } & IdPhotoPresence) | null;
};

/** One read of the response, so the nullable fields are interpreted in exactly one place. */
const readEntry = (entry: EnteredEntry | null | undefined) => {
  const identifiedId = entry?.ValidatedIdentificationCustomer?.Id ?? 0;

  return {
    /** A profile this scan created. Unambiguous: it exists because of this scan. */
    created: entry?.CreatedCustomerId ?? null,
    checkedIn: Boolean(entry?.CheckedInShipmentId),
    /** The identified customer's existing profile, or null when the scan matched nobody. */
    existingId: identifiedId > 0 ? identifiedId : null,
    /** The list as the server sent it, which is what says who it named. */
    matches: entry?.ValidatedMatchedCustomers ?? [],
    /** The same list minus the candidates nobody can select, which is what says who it checked in. */
    selectable: entry?.ValidatedMatchedCustomers?.filter((candidate) => !candidate.IsBanned) ?? [],
  };
};

/**
 * The one resolver for the id the optional front photo is written to: a profile this scan created, then the
 * customer the scan resolved to, then the top selectable match. Null on the decision path, because the
 * candidates are still guesses and a photo written to one staff then reject sits on the wrong record.
 */
export const enteredCustomerId = (entry: EnteredEntry | null | undefined): number | null => {
  const { created, checkedIn, existingId, matches, selectable } = readEntry(entry);
  if (created) {
    return created;
  }
  if (!checkedIn) {
    return null;
  }

  if (existingId) {
    // Who the scan resolved to wins wherever the list ranked them, as in `decodeScanEntry`. Membership is
    // tested against the list the server sent, not the selectable subset: whether it named this customer has
    // nothing to do with whether the *other* candidates are banned. A list that omits them is a real
    // disagreement, and the two failures are not symmetric — withholding a photo is recoverable and visible,
    // writing one to the wrong customer is neither.
    const listed = matches.length === 0 || matches.some((candidate) => candidate.Id === existingId);

    return listed ? existingId : null;
  }

  return selectable[0]?.Id ?? null;
};

export type ScanEntryOutcome =
  /**
   * The pipeline finished. `photoCustomerId` is only who to offer the photo save for — never a
   * general "the customer this screen is about". Named for that single use because the narrower
   * meaning is invisible at the call site otherwise: it is null whenever attribution is not safe,
   * including for check-ins that definitely landed on somebody.
   */
  | { kind: 'checkedIn'; photoCustomerId: number | null }
  /** Staff choose. At least one action is always available, and `createFrom` is the profile that does not exist yet. */
  | {
      kind: 'decision';
      candidates: ScanValidatedCustomer[];
      createFrom: ScanValidatedCustomer | null;
      /** What the scan read, shown however the decision resolves. */
      identified: ScanValidatedCustomer | null;
    }
  /** The pipeline neither checked anyone in nor gave staff anything to act on. */
  | { kind: 'unresolved' };

/**
 * Routes a response to the one thing staff can do with it. A profile that already exists — created by this
 * scan, or resolved to by it — is offered for check-in and never for creation, so a second tap cannot
 * duplicate it. Fail-closed throughout: a shape that names no action at all is `unresolved` rather than a
 * decision with nothing in it, and a created profile can never come back as an offer to create.
 */
export const decodeScanEntry = (entry: ScanGuestEntryResponse | null | undefined): ScanEntryOutcome => {
  const { created, checkedIn, existingId } = readEntry(entry);
  if (checkedIn) {
    return { kind: 'checkedIn', photoCustomerId: enteredCustomerId(entry) };
  }

  const identified = entry?.ValidatedIdentificationCustomer ?? null;
  const matches = entry?.ValidatedMatchedCustomers ?? [];

  if (created) {
    // The new profile is a candidate under its own id, so a second tap checks it in. With no identification
    // to name it — a shape with no backend contract either way — only the matches are left to offer.
    const candidates = identified ? [{ ...identified, Id: created }] : matches;

    return candidates.length === 0
      ? { kind: 'unresolved' }
      : { kind: 'decision', candidates, createFrom: null, identified };
  }

  const existing = existingId ? identified : null;
  // The customer the scan resolved to leads wherever the server ranked them, and appears once. The rest keep
  // the server's ranking, banned entries included, because both surfaces refuse a banned candidate visibly.
  const candidates = existing ? [existing, ...matches.filter((candidate) => candidate.Id !== existing.Id)] : matches;
  const createFrom = existing ? null : identified;

  if (candidates.length === 0 && !createFrom) {
    return { kind: 'unresolved' };
  }

  return { kind: 'decision', candidates, createFrom, identified };
};
